import { useState } from "react";
import { Languages, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";

const FR_WORDS = new Set("le la les un une des du de et est sont avec pour dans sur pas très je vous nous il elle ce cette ces mon ma mes ton sa son qui que au aux ou mais à vendre chiot chiots chaton chatons ans mois semaines".split(" "));
const EN_WORDS = new Set("the a an and is are with for in on not very i you we he she it this these my your his her who that to of or but sale puppy puppies kitten kittens years months weeks old".split(" "));

// Guess whether an ad is written in French or English from its words.
export const detectLang = (text) => {
  const words = (text || "").toLowerCase().match(/[a-zàâçéèêëîïôûùüÿœæ']+/g) || [];
  let fr = 0;
  let en = 0;
  words.forEach((w) => { if (FR_WORDS.has(w)) fr += 1; if (EN_WORDS.has(w)) en += 1; });
  fr += ((text || "").match(/[àâçéèêëîïôûùœ]/gi) || []).length * 0.5;
  return fr > en ? "fr" : "en";
};

export const useListingTranslation = (listingId, lang, text, listing = null) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showing, setShowing] = useState(false);
  const [hideAlt, setHideAlt] = useState(false);
  const src = listing?.src_lang || detectLang(text);
  const target = src === "fr" ? "en" : "fr";
  const alt = listing?.bilingual && listing.alt_lang === lang && listing.title_alt
    ? { title: listing.title_alt, description: listing.description_alt, lang, seller: true } : null;
  const fresh = data && data.lang === target && data.id === listingId;
  const active = alt ? (hideAlt ? null : alt) : (showing && fresh ? data : null);
  const run = async () => {
    if (alt) return setHideAlt(false);
    if (fresh) return setShowing(true);
    setBusy(true);
    try {
      const r = await api.post(`/listings/${listingId}/translate?lang=${target}`);
      if (r.data.same) toast(lang === "fr" ? "Aucune traduction nécessaire pour cette annonce" : "This ad doesn't need translating");
      else { setData({ ...r.data, id: listingId }); setShowing(true); }
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const hide = () => (alt ? setHideAlt(true) : setShowing(false));
  return { active, busy, run, hide, fr: lang === "fr", src, target };
};

const LABEL = {
  en: { fr: "Translate to French", en: "Translate to English", orig: "Show original", from: { en: "Automatically translated from English", fr: "Automatically translated from French" } },
  fr: { fr: "Traduire en français", en: "Traduire en anglais", orig: "Voir l'original", from: { en: "Traduit automatiquement de l'anglais", fr: "Traduit automatiquement du français" } },
};

export const ListingTranslateButton = ({ t }) => {
  const L = LABEL[t.fr ? "fr" : "en"];
  return (
    <button type="button" onClick={t.active ? t.hide : t.run} disabled={t.busy} data-testid="listing-translate-btn" data-target-lang={t.target}
      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-[#12234A] hover:border-[#F5551E] hover:text-[#F5551E] transition-colors disabled:opacity-60">
      {t.busy ? <Loader2 size={13} className="animate-spin" /> : <Languages size={13} />}
      {t.active ? L.orig : L[t.target]}
    </button>
  );
};

export const TranslatedNote = ({ t }) => t.active ? (
  <p className="mt-2 text-xs text-slate-400 flex items-center gap-1" data-testid="listing-translated-note">
    <Languages size={12} /> {LABEL[t.fr ? "fr" : "en"].from[t.src]}{t.active.seller ? (t.fr ? " · le vendeur a publié cette annonce dans les deux langues" : " · the seller posted this ad in both languages") : ""}
  </p>
) : null;
